import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode, SyntheticEvent } from 'react';
import type { AccessGateway } from '../application/accessGateway';
import {
  beginFounderAccess,
  redeemInvitation,
  resumePendingAccess,
} from '../application/accessUseCases';
import type { AccessResult } from '../domain/access';
import styles from './AccessScreen.module.css';

interface AccessScreenProps {
  readonly gateway: AccessGateway;
  readonly navigateToDashboard?: () => void;
}

type FlowState = 'idle' | 'submitting' | 'pending' | 'error' | 'success';

type StatusKind = 'error' | 'success' | 'info';

function BrandMark(): React.JSX.Element {
  return (
    <div className={styles.brand} aria-label="Alpha MLS">
      <span>ALPHA</span>
      <span className={styles.brandDot} aria-hidden="true" />
      <span className={styles.brandMls}>MLS</span>
    </div>
  );
}

function StatusMessage({
  kind,
  children,
}: {
  readonly kind: StatusKind;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <div
      className={styles.message}
      data-kind={kind}
      role={kind === 'error' ? 'alert' : 'status'}
    >
      {children}
    </div>
  );
}

function defaultNavigateToDashboard(): void {
  window.location.assign('/dashboard');
}

function messageKind(state: FlowState): StatusKind {
  if (state === 'success') return 'success';
  if (state === 'pending') return 'info';
  return 'error';
}

export function AccessScreen({
  gateway,
  navigateToDashboard = defaultNavigateToDashboard,
}: AccessScreenProps): React.JSX.Element {
  const [founderState, setFounderState] = useState<FlowState>('idle');
  const [founderMessage, setFounderMessage] = useState<string | null>(null);
  const [invitationState, setInvitationState] = useState<FlowState>('idle');
  const [invitationMessage, setInvitationMessage] = useState<string | null>(
    null,
  );
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleResult = useCallback(
    (
      result: AccessResult,
      setState: (value: FlowState) => void,
      setMessage: (value: string | null) => void,
    ): void => {
      if (!result.ok) {
        setState(result.pending === true ? 'pending' : 'error');
        setMessage(result.error.message);
        return;
      }

      setState('success');
      setMessage(`Identidad verificada para ${result.identity.displayName}.`);
      navigateToDashboard();
    },
    [navigateToDashboard],
  );

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    void resumePendingAccess(gateway, controller.signal)
      .then((result) => {
        if (result === null || controller.signal.aborted) return;

        if (result.ok && result.identity.actor === 'founder') {
          handleResult(result, setFounderState, setFounderMessage);
          return;
        }

        handleResult(result, setInvitationState, setInvitationMessage);
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          setInvitationState('error');
          setInvitationMessage(
            'La reanudación de la sesión se interrumpió de forma inesperada.',
          );
        }
      });

    return () => controller.abort();
  }, [gateway, handleResult]);

  async function handleFounderAccess(): Promise<void> {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setFounderState('submitting');
    setFounderMessage(null);

    try {
      const result = await beginFounderAccess(gateway, controller.signal);
      handleResult(result, setFounderState, setFounderMessage);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        setFounderState('error');
        setFounderMessage(
          'El flujo de identidad se interrumpió de forma inesperada.',
        );
      }
    }
  }

  async function handleInvitationSubmit(
    event: SyntheticEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setInvitationState('submitting');
    setInvitationMessage(null);

    try {
      const result = await redeemInvitation(
        gateway,
        { email, token },
        controller.signal,
      );
      handleResult(result, setInvitationState, setInvitationMessage);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        setInvitationState('error');
        setInvitationMessage(
          'El flujo de invitación se interrumpió de forma inesperada.',
        );
      }
    }
  }

  return (
    <main className={styles.shell}>
      <div className={styles.ambient} aria-hidden="true" />
      <header className={styles.header}>
        <BrandMark />
        <span className={styles.environment}>Acceso seguro</span>
      </header>

      <section className={styles.hero} aria-labelledby="access-heading">
        <div className={styles.copy}>
          <p className={styles.eyebrow}>
            Plataforma profesional de colaboración inmobiliaria
          </p>
          <h1 id="access-heading">
            Tu operación empieza con una identidad verificable.
          </h1>
          <p className={styles.lede}>
            Elige el flujo que corresponde a tu relación con Alpha•MLS. Los
            permisos se resuelven en servidor y nunca desde esta pantalla.
          </p>
        </div>

        <div className={styles.grid}>
          <article className={styles.card}>
            <div>
              <p className={styles.cardKicker}>Fundador</p>
              <h2>Acceso institucional</h2>
              <p className={styles.cardText}>
                Usa la identidad verificada por el host. Este flujo no utiliza
                invitaciones comerciales.
              </p>
            </div>

            <button
              className={styles.primaryButton}
              type="button"
              disabled={founderState === 'submitting'}
              onClick={() => void handleFounderAccess()}
            >
              {founderState === 'submitting'
                ? 'Verificando…'
                : 'Entrar como Fundador'}
            </button>

            {founderMessage !== null ? (
              <StatusMessage kind={messageKind(founderState)}>
                {founderMessage}
              </StatusMessage>
            ) : (
              <StatusMessage kind="info">
                Si el host no entrega identidad verificable, el acceso
                permanece cerrado.
              </StatusMessage>
            )}

            {founderState === 'pending' ? (
              <div className={styles.form}>
                <p className={styles.cardText}>
                  Abre el correo más reciente de Supabase Auth y{' '}
                  <strong>pulsa Sign in</strong>. El enlace regresará a
                  Alpha•MLS y completará la sesión en este navegador.
                </p>
              </div>
            ) : null}
          </article>

          <article className={styles.card}>
            <div>
              <p className={styles.cardKicker}>Cliente o invitado</p>
              <h2>Acceso por invitación</h2>
              <p className={styles.cardText}>
                Tu invitación es individual. El correo debe coincidir con el
                registro autorizado.
              </p>
            </div>

            <form
              className={styles.form}
              onSubmit={(event) => void handleInvitationSubmit(event)}
            >
              <label className={styles.label} htmlFor="invitation-email">
                Correo asociado
              </label>
              <input
                className={styles.input}
                id="invitation-email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.currentTarget.value)}
                placeholder="nombre@empresa.com"
                required
              />

              <label className={styles.label} htmlFor="invitation-token">
                Código de invitación
              </label>
              <input
                className={styles.input}
                id="invitation-token"
                name="token"
                type="text"
                autoComplete="one-time-code"
                value={token}
                onChange={(event) => setToken(event.currentTarget.value)}
                placeholder="Pega aquí tu código"
                required
              />

              <button
                className={styles.secondaryButton}
                type="submit"
                disabled={invitationState === 'submitting'}
              >
                {invitationState === 'submitting'
                  ? 'Validando…'
                  : 'Validar invitación'}
              </button>
            </form>

            {invitationMessage !== null ? (
              <StatusMessage kind={messageKind(invitationState)}>
                {invitationMessage}
              </StatusMessage>
            ) : (
              <StatusMessage kind="info">
                Una invitación inválida o vencida nunca concede acceso parcial.
              </StatusMessage>
            )}
          </article>
        </div>
      </section>

      <footer className={styles.footer}>
        <span>Alpha•MLS</span>
        <a href="/plans">Ver planes</a>
        <span>Identidad y autorización separadas desde el origen.</span>
      </footer>
    </main>
  );
}
